import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../utils/api';

/** The three cone-calorimeter fuel analogues every coefficient is keyed by. */
export type RetardantFuelCategory = 'fine_flashy' | 'needle_litter' | 'coarse_woody';
export type CategoryMap = Partial<Record<RetardantFuelCategory, number>>;

/** What an ordinary user sees in the treatment modal's product picker. */
export interface RetardantProductOption {
  key: string;
  name: string;
  description: string;
  half_life_days: number;
}

/** What a user submits from the treatment modal's "Add retardant product". */
export interface RetardantProductInput {
  name: string;
  description: string;
  combustion_efficiency: CategoryMap;
  available_fuel: CategoryMap;
  ignition_delay: CategoryMap;
  suppresses_ignition: RetardantFuelCategory[];
  half_life_days: number;
  residual_efficacy_floor: number;
  persistence_horizon_days: number;
}

interface DataEnvelope<T> {
  data: T;
}

const PICKER_KEY = ['retardant-products'];

/** The treatment picker: shared products plus this organisation's own. */
export const useRetardantProducts = () =>
  useQuery({
    queryKey: PICKER_KEY,
    queryFn: async () => {
      const res = await api.request<DataEnvelope<RetardantProductOption[]>>(
        '/api/retardant-products'
      );
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });

/** Add a product for the current tenant; it is visible there immediately. */
export const useCreateRetardantProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: RetardantProductInput) => {
      const res = await api.request<DataEnvelope<RetardantProductOption>>(
        '/api/retardant-products',
        { method: 'POST', body: input }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PICKER_KEY });
    },
  });
};
